import {
  CatchmentAreaOriginHomeH3,
  CustomFootfall,
  YYYYMMDDDateString,
  DayOfWeekHourTrends,
  DayOfWeekTrends,
  FootTrafficTrends,
  MultiPolygon,
  Polygon,
} from 'src/common-services'
import { MapShape } from 'src/types/map-editor'
import { Colors } from 'src/components/theme'
import { customFootfallClient } from 'src/clients'
import { rgbaStrOpacity } from 'src/utils/colors'
import { wait } from 'src/utils/wait'

// Creates a new polygon feature with a given geometry
export const getPolygonFeature = (
  geometry: Polygon | MultiPolygon,
  color?: string,
  id = 'polygon',
  properties?: GeoJSON.GeoJsonProperties
): MapShape => {
  return {
    type: 'Feature',
    geometry: geometry,
    id: id,
    properties: {
      id: id,
      text: 'polygon',
      type: 'polygon',
      fill: rgbaStrOpacity(color || Colors.blueMedium, 0.25),
      stroke: color || Colors.blueMedium,
      strokeWidth: 2,
      zIndex: 1,
      ...properties,
    },
  }
}

export const getMMMYYYDate = (date: YYYYMMDDDateString): string => {
  const dateString = new Date(date).toLocaleString('default', {
    timeZone: 'UTC',
    month: 'short',
    year: 'numeric',
  })
  return dateString
}

type AnyDataSet = FootTrafficTrends | DayOfWeekHourTrends | DayOfWeekTrends | CatchmentAreaOriginHomeH3
export type PolledCustomFootfall = Record<string, CustomFootfall<AnyDataSet>>

export const retryCustomPolygonRequest = async <T extends AnyDataSet>(
  request: () => Promise<CustomFootfall<T>>,
  maxRetries = 5
): Promise<CustomFootfall<T>> => {
  let retries = 0
  while (true) {
    try {
      return await request()
    } catch (e: any) {
      if (retries >= maxRetries) {
        throw e
      }
      retries++
      await wait(10000)
    }
  }
}

export const pollCustomFootfall = async function (
  reportIds: string[],
  ms: number,
  signal: AbortSignal
): Promise<PolledCustomFootfall | undefined> {
  let fetchResults: (CustomFootfall<AnyDataSet> | undefined)[] = await Promise.all(
    reportIds.map((reportId) => customFootfallClient.getCustomFootfall<AnyDataSet>(reportId))
  )
  let results: PolledCustomFootfall = fetchResults.reduce((acc: any, result) => {
    if (!result) {
      return acc
    }
    acc[result.reportId] = result
    return acc
  }, {})

  while (fetchResults.some((result) => result && result.status === 'pending') && !signal.aborted) {
    await wait(ms)
    fetchResults = await Promise.all(
      reportIds.map(async (reportId) => {
        if (signal.aborted) {
          return undefined
        }
        await wait(200)
        const resp = retryCustomPolygonRequest<AnyDataSet>(
          async () => await customFootfallClient.getCustomFootfall<AnyDataSet>(reportId),
          10
        )
        return resp
      })
    )

    results = fetchResults.reduce((acc: any, result) => {
      if (!result) {
        return acc
      }
      acc[result.reportId] = result
      return acc
    }, {})
  }
  if (signal.aborted) {
    return undefined
  }
  return results
}
